import { useEffect, useState } from "react";
import {
  Card,
  Center,
  Container,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import { Link } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import { ref, onValue, update } from "firebase/database";
import { db } from "../firebase";
import { FIREBASE_DB_ROOT } from "../App";

type GeneralSettings = {
  enabled: boolean;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
};

const generalRef = () => ref(db, `${FIREBASE_DB_ROOT}/general`);

const SettingSwitch = ({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) => (
  <Card
    withBorder
    radius="md"
    padding="lg"
    style={{ opacity: disabled ? 0.5 : 1 }}
  >
    <Group justify="space-between" align="center" wrap="nowrap">
      <Stack gap="xs">
        <Text fw={600}>{label}</Text>
        <Text size="sm" color="dimmed">
          {description}
        </Text>
      </Stack>
      <Switch
        size="lg"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
    </Group>
  </Card>
);

export const Settings = () => {
  const [settings, setSettings] = useState<GeneralSettings | null>(null);

  useEffect(() => {
    const unsubscribe = onValue(generalRef(), (snapshot) => {
      const data = snapshot.val() ?? {};
      setSettings({
        enabled: data.enabled !== false,
        // Missing flags mean enabled, same as the mobile app's defaults
        deliveryEnabled: data.deliveryEnabled !== false,
        pickupEnabled: data.pickupEnabled !== false,
      });
    });

    return unsubscribe;
  }, []);

  const setFlag = (key: keyof GeneralSettings, value: boolean) => {
    update(generalRef(), { [key]: value }).catch((error) => {
      showNotification({
        title: "Greška",
        message: `Spremanje nije uspjelo: ${error.message}`,
        color: "red",
      });
    });
  };

  return (
    <Container size="md" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Postavke</Title>
          <Text size="sm" color="dimmed">
            <Link to="/" style={{ textDecoration: "underline" }}>
              Natrag na narudžbe
            </Link>
          </Text>
        </Group>

        {settings === null ? (
          <Center>
            <Loader variant="dots" />
          </Center>
        ) : (
          <>
            <SettingSwitch
              label="Aplikacija"
              description="Glavni prekidač — kada je isključeno, aplikacija prikazuje poruku da je zatvoreno."
              checked={settings.enabled}
              onChange={(value) => setFlag("enabled", value)}
            />
            <SettingSwitch
              label="Dostava"
              description="Kada je isključeno, u aplikaciji je moguće samo preuzimanje u objektu."
              checked={settings.deliveryEnabled}
              disabled={!settings.enabled}
              onChange={(value) => setFlag("deliveryEnabled", value)}
            />
            <SettingSwitch
              label="Preuzimanje u objektu"
              description="Kada je isključeno, preuzimanje u objektu nije dostupno."
              checked={settings.pickupEnabled}
              disabled={!settings.enabled}
              onChange={(value) => setFlag("pickupEnabled", value)}
            />
          </>
        )}
      </Stack>
    </Container>
  );
};
